"use client";
import { cn } from "@/lib/utils";
import { ThemeParticles } from "./event-theme-particles";
import { ThemeVideo } from "./event-theme-video";
import { ThemePattern } from "./event-theme-pattern";
import { useIsMounted, useMediaQuery } from "usehooks-ts";
import AdditionalCss from "../ui/additional-css";
import { BlurhashCanvas } from "react-blurhash";
import { safeJsonParse } from "@/lib/safeJsonParse";
import { Gradient } from "@/lib/whatamesh";
import { useEffect } from "react";

export default function EventThemeApplyer({
  event,
  inSheet,
  theme,
  additionalCss,
  className,
}: any) {
  const isMounted = useIsMounted();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  function extractHexValues(input: string): string[] {
    // Regular expression to match hex values (6 characters each starting with '#')
    const hexRegex = /#[0-9A-Fa-f]{6}/g;

    // Extract all hex values from the input string
    const matches: string[] = input.match(hexRegex) || [];

    // Ensure we always have four values by filling missing ones with '#000000'
    while (matches.length < 4) {
      matches.push("#000000");
    }
    return matches.slice(0, 4); // Ensure only 4 values are returned
  }

  useEffect(() => {
    if (theme?.mode == "sunrise" && !inSheet) {
      const gradient = new Gradient() as any;
      gradient.initGradient("#gradient-canvas");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <>
      <style>
        {theme &&
          theme.background &&
          `
          #gradient-canvas {
            --gradient-color-1: ${extractHexValues(theme.background)[0]};
            --gradient-color-2: ${extractHexValues(theme.background)[1]};
            --gradient-color-3: ${extractHexValues(theme.background)[2]};
            --gradient-color-4: ${extractHexValues(theme.background)[3]};
          }
        `}
      </style>
      {event && !!!safeJsonParse(event.image)?.blurhash && inSheet && (
        <BlurhashCanvas
          hash={"LEHV6nWB2yk8pyo0adR*.7kCMdnj"}
          punch={1}
          className={cn(
            " w-screen h-screen -z-10 opacity-30 saturate-200 top-0",
            inSheet ? "absolute" : "fixed",
          )}
        />
      )}

      {event && safeJsonParse(event.image)?.blurhash && (
        <BlurhashCanvas
          hash={safeJsonParse(event.image).blurhash}
          punch={1}
          className={cn(
            " w-screen h-screen -z-10 opacity-30 saturate-200 top-0",
            inSheet ? "absolute" : "fixed",
          )}
        />
      )}

      {theme && (
        <div>
          {[
            "minimal",
            "warp",
            "emoji",
            "confetti",
            "season",
            "sunrise",
          ].includes(theme.mode) && (
            <div
              className={cn(
                !inSheet ? "opacity-0 transition-opacity duration-200 " : "",
                className,
                isMounted() ? "opacity-30 saturate-200" : "",
                "flex fade-in top-0 min-h-screen h-screen w-full object-cover -z-50 background-force-cover",
                inSheet ? "absolute" : "fixed",
              )}
              style={{ background: theme.background }}
            ></div>
          )}

          <canvas
            id="gradient-canvas"
            className={cn(
              "fixed top-0 w-screen h-screen -z-50 dark:opacity-50",
              theme.mode != "sunrise" && "hidden",
              !isDesktop && "hidden",
              inSheet && "hidden",
            )}
          ></canvas>

          {theme.mode == "pattern" && (
            <ThemePattern
              patternName={theme.pattern?.name}
              patternSize={theme.pattern.size}
              patternColor={theme.pattern.color}
              patternSecondaryColor={theme.pattern.secondaryColor}
              patternOpacity={theme.pattern.opacity}
              inSheet={inSheet}
            />
          )}

          {isDesktop && !inSheet && theme.mode == "video" && (
            <ThemeVideo
              backgroundVideo={theme.backgroundVideo}
              inSheet={inSheet}
            />
          )}

          {isDesktop &&
            !inSheet &&
            ["emoji", "confetti", "season"].includes(theme.mode) && (
              <ThemeParticles
                particlesOptions={theme.particles.type}
                emojis={theme.particles.emojis}
                inSheet={inSheet}
              />
            )}
          {isDesktop && !inSheet && theme.mode == "warp" && (
            <ThemeParticles particlesOptions={"warp"} inSheet={inSheet} />
          )}

          {!inSheet && <style>{theme.fontPayload}</style>}

          {!inSheet && <AdditionalCss payload={additionalCss} />}
        </div>
      )}
    </>
  );
}
